import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';
import AuthManager from '../../../../../Common/AuthManager';
import { AppContext } from '../../../../../Context/AppContext';
import { TUpsertISRProjectDataModelsInput } from '../../Pages/DefineModel/model.d';

type ViewType = 'list' | 'editor';

/** Instance limits for a submodel, sourced from the Data Model spec (TSubmodel.data). */
export type SubmodelInstanceLimits = { maxInstantiation: number; minInstantiation: number };

/** Fallback when a submodel id has no spec entry (e.g. unsaved). Max=1 is the safe default. */
export const DEFAULT_INSTANCE_LIMITS: SubmodelInstanceLimits = { maxInstantiation: 1, minInstantiation: 1 };

type ModuleContextType = {
  activeView: ViewType;
  setActiveView: (view: ViewType) => void;
  industryId: string;
  userId: string;
  hasEditPermission: () => boolean;
  userProjectDataModels: TUpsertISRProjectDataModelsInput | null;
  /** submodelId → instance limits (max/min), derived once from the Data Model spec. */
  submodelInstanceLimits: Map<string, SubmodelInstanceLimits>;
  /** Resolve a submodel's instance limits by id; returns DEFAULT_INSTANCE_LIMITS when unknown. */
  getSubmodelInstanceLimits: (submodelId: string | null | undefined) => SubmodelInstanceLimits;
  /** Resolve a submodel's display name (label || name) by id; null when unknown. */
  getSubmodelName: (submodelId: string | null | undefined) => string | null;
};

const ModuleContext = createContext<ModuleContextType | undefined>(undefined);

export const ModuleProvider = ({ children, industryId = '', userId = '', userProjectDataModels = null }: {
  children: React.ReactNode;
  industryId?: string;
  userId?: string;
  userProjectDataModels?: TUpsertISRProjectDataModelsInput | null;
}) => {
  const [activeView, setActiveView] = useState<ViewType>('list');
  const appData = useContext(AppContext);
  const hasEditPermission = () => {
    const hasRolePermission = appData?.pm?.checkPermission("gmsguide_update") ?? false;
    const isOwner = userId === AuthManager.getAuthUser().profile?.id;
    return hasRolePermission && isOwner;
  };

  // Derive submodelId → instance limits from the Data Model spec. Rebuilt only
  // when the spec reference changes. Lets container-locking rules resolve a
  // submodel's max/min by id without re-scanning userProjectDataModels everywhere.
  const submodelInstanceLimits = useMemo(() => {
    const map = new Map<string, SubmodelInstanceLimits>();
    const dataModels = userProjectDataModels?.project?.dataModels ?? [];
    for (const submodel of dataModels) {
      if (!submodel?.id) continue;
      map.set(submodel.id, {
        maxInstantiation: submodel.data?.maxInstantiation ?? 1,
        minInstantiation: submodel.data?.minInstantiation ?? 1,
      });
    }
    return map;
  }, [userProjectDataModels]);

  const getSubmodelInstanceLimits = useCallback(
    (submodelId: string | null | undefined): SubmodelInstanceLimits =>
      (submodelId ? submodelInstanceLimits.get(submodelId) : undefined) ?? DEFAULT_INSTANCE_LIMITS,
    [submodelInstanceLimits]
  );

  // submodelId → display name (label || name), from the same Data Model spec.
  // Lets UI derive a container's lock name from a submodel id without trusting a
  // possibly-stale content.submodel binding.
  const submodelNames = useMemo(() => {
    const map = new Map<string, string>();
    const dataModels = userProjectDataModels?.project?.dataModels ?? [];
    for (const submodel of dataModels) {
      if (!submodel?.id) continue;
      const name = submodel.data?.label || submodel.data?.name;
      if (name) map.set(submodel.id, name);
    }
    return map;
  }, [userProjectDataModels]);

  const getSubmodelName = useCallback(
    (submodelId: string | null | undefined): string | null =>
      (submodelId ? submodelNames.get(submodelId) : undefined) ?? null,
    [submodelNames]
  );

  return (
    <ModuleContext.Provider
      value={{
        activeView,
        setActiveView,
        industryId,
        userId,
        hasEditPermission,
        userProjectDataModels,
        submodelInstanceLimits,
        getSubmodelInstanceLimits,
        getSubmodelName,
      }}
    >
      {children}
    </ModuleContext.Provider>
  );
};

export const useModule = () => {
  const context = useContext(ModuleContext);
  if (context === undefined) {
    throw new Error('useModule must be used within a ModuleProvider');
  }
  return context;
};

export default ModuleContext;
